<template>
  <v-container v-if="!deal" fluid>
    <v-progress-linear :indeterminate="true" />
  </v-container>
  <v-container v-else fluid>
    <div class="d-flex justify-space-between">
      <v-btn @click="$router.go(-1)">Back To Deals</v-btn>
      <v-btn @click="deleteDeal(deal.id)" color="red">Delete</v-btn>
    </div>
    <v-row>
      <v-col cols="8">
        <h3>Deal</h3>
        <v-card>
          <v-card-text>
            <v-simple-table>
              <tbody>
                <tr>
                  <td>Created at</td>
                  <td>{{ deal.createdAt | readableDateTime }}</td>
                </tr>
                <tr>
                  <td>Closed at</td>
                  <td>
                    <span v-if="deal.closedAt">{{ deal.closedAt | readableDateTime }}</span>
                  </td>
                </tr>
                <tr>
                  <td>Client</td>
                  <td>{{ clientName }}</td>
                </tr>
                <tr>
                  <td>Deal Owner</td>
                  <td>
                    <v-select
                      :value="deal.dealOwner ? deal.dealOwner.id : null"
                      :items="
                        dealOwners.map((element) => {
                          return { text: element.user.firstName + ' ' + element.user.lastName, value: element.id };
                        })
                      "
                      @input="setDealOwner($event, deal.id)"
                    />
                  </td>
                </tr>
                <tr>
                  <td>Stage</td>
                  <td>
                    <v-select :value="deal.stage.id" :items="stages" @input="setStage($event, deal.id)" />
                  </td>
                </tr>
                <tr>
                  <td>Probability</td>
                  <td>
                    <v-select
                      :value="deal.probability"
                      :items="options.probabilities"
                      @input="setProbability($event, deal.id)"
                    />
                  </td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td>{{ clientPhone }}
                  </td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td>{{ clientEmail }}</td>
                </tr>
                <tr>
                  <td>Note</td>
                  <td>
                    <v-textarea v-model="deal.note" rows="1" auto-grow @input="noteInput()"> </v-textarea>
                  </td>
                </tr>
                <tr>
                  <td>Attribution</td>
                  <td>
                    <div v-if="deal.attributionCampaign">Campaign: {{ deal.attributionCampaign }}</div>
                    <div v-if="deal.attributionMedium">Medium: {{ deal.attributionMedium }}</div>
                    <div v-if="deal.attributionSource">Source: {{ deal.attributionSource }}</div>
                  </td>
                </tr>
                <tr>
                  <td>Last Call</td>
                  <td>{{ deal.lastCallTime | readableDateTime }}</td>
                </tr>
                <tr>
                  <td>Timeline</td>
                  <td><v-btn type="primary" @click="showTimelineDialog = true">Show Timeline</v-btn></td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
        <h3 class="mt-8 mb-n2">Logged Calls</h3>
        <deal-logged-call-list
          class="mt-3"
          :show-actions="false"
          :call-outcomes="options.callOutcomes"
          :calls="dateSortedCallList"
          @delete-call="deleteCall($event)"
          @edit-call="editCall($event)"
        />
        <v-btn @click="logCall(deal)" class="primary mt-4">Log Call</v-btn>
      </v-col>
      <v-col cols="4">
        <user-information v-if="deal.contact.user" :userId="deal.contact.user.id" :compact-view="true" />
        <v-card v-else>
          <v-card-title>Contact Information</v-card-title>
          <v-card-text>
            <strong>This contact is not linked to a user account with JEFF.</strong>
            <v-autocomplete
              v-if="linkUser.show"
              v-model="linkUser.select"
              :loading="linkUser.loadingSearchResults"
              :search-input.sync="search"
              :items="linkUser.results"
              label="Select User"
              @change="onContactChangeSelect"
              return-object
              hide-details
            />
            <div v-else>
              <v-alert type="info">Link this contact to: {{ linkUser.select.text }}?</v-alert>
              <v-btn color="primary" @click="onContactChangeConfirm">Confirm</v-btn>
              <v-btn color="error" class="ml-5" @click="onContactChangeCancel">Cancel</v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showTimelineDialog" fullscreen>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showTimelineDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <deal-timeline :deal="deal" />
      </v-card>
    </v-dialog>
    <v-dialog v-if="logCallDialog" v-model="logCallDialog" width="500">
      <deal-log-call
        ref="logCallForm"
        :call-outcomes="options.callOutcomes"
        :call-types="dealConfiguration.callTypes"
        :deal-owners="dealOwners"
        :stages="dealConfiguration.stages"
        @insert-logged-call="insertLoggedCall"
        @edit-logged-call="editLoggedCall"
        @update-stage="setStage"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { DEAL_QUERY, DEAL_USER_SEARCH_QUERY } from "@/graphql/queries/deal";
import DealManagementMixin from "@/components/deals/mixins/DealManagementMixin";
import UserInformation from "@/components/client/blocks/UserInformation";
import DealLoggedCallList from "@/components/deals/components/DealLoggedCallList";
import DealLogCall from "@/components/deals/components/DealLogCall";
import { decodeId } from "@/lib/string";
import DealTimeline from "@/components/deals/components/DealTimeline";
import { DELETE_DEAL_MUTATION } from "@/graphql/mutations/deal";
import { debounce } from "@/lib/helpers";

export default {
  name: "DealView",
  mixins: [DealManagementMixin],
  components: {
    DealTimeline,
    UserInformation,
    DealLoggedCallList,
    DealLogCall,
  },
  data() {
    return {
      showTimelineDialog: false,
      dealId: null,
      deal: null,
      search: null,
      linkUser: {
        show: true,
        select: null,
        loadingSearchResults: false,
        results: [],
      },
    };
  },
  created() {
    this.dealId = decodeId(this.$route.params.id);
  },
  watch: {
    search(val) {
      if (!val) return;
      val && val !== this.select && val.length > 2 && this.searchUsers(val);
    },
  },
  computed: {
    dateSortedCallList() {
      if (!this.deal) return [];
      let data = this.deal.dealcallSet;
      return data.sort((a, b) => (a.callTime < b.callTime ? 1 : -1));
    },
    clientName() {
      return `${this.deal.contact.firstName} ${this.deal.contact.lastName}`;
    },
    clientEmail() {
      return this.deal.contact.email;
    },
    clientPhone() {
      return this.deal.contact.phone;
    },
  },
  methods: {
    async setDealOwner(ownerId, id) {
      await this.$super(DealManagementMixin).setDealOwner(ownerId, id);
      await this.$apollo.queries.deal.refetch();
    },
    async setStage(stage, id) {
      await this.$super(DealManagementMixin).setStage(stage, id);
      await this.$apollo.queries.deal.refetch();
    },
    async setProbability(probability, id) {
      await this.$super(DealManagementMixin).setProbability(probability, id);
      await this.$apollo.queries.deal.refetch();
    },
    noteInput() {
      debounce(this.setNote, 1000);
    },
    async setNote() {
      await this.$super(DealManagementMixin).setNote(this.deal.note, this.deal.id);
      await this.$apollo.queries.deal.refetch();
    },
    async deleteCall(call) {
      let deleted = await this.$super(DealManagementMixin).deleteCall(call);
      if (!deleted) return;
      await this.$apollo.queries.deal.refetch();
    },
    async logCall(item) {
      this.logCallDialog = true;
      await this.$nextTick();
      this.$refs.logCallForm.init(item, decodeId(this.$store.getters.user.id));
    },
    async editCall(call) {
      this.logCallDialog = true;
      await this.$nextTick();
      this.$refs.logCallForm.initEdit(this.deal, call);
    },
    async insertLoggedCall(data) {
      await this.$super(DealManagementMixin).insertLoggedCall(data);
      await this.$apollo.queries.deal.refetch();
    },
    async editLoggedCall(data) {
      await this.$super(DealManagementMixin).editLoggedCall(data);
      await this.$apollo.queries.deal.refetch();
    },
    async deleteDeal(id) {
      let confirm = window.confirm("Are you sure you want to delete this deal?");
      if (!confirm) return;
      let result = await this.$apollo.mutate({
        mutation: DELETE_DEAL_MUTATION,
        variables: {
          dealId: decodeId(id),
        },
      });
      if (result.data.deleteDealMutation.success) this.showSuccessMessage("Deal deleted");
      setTimeout(() => {
        this.$router.push("/deals");
      }, 1000);
    },
    async searchUsers(searchVal) {
      this.linkUser.results = [];
      this.linkUser.loadingSearchResults = true;
      let results = await this.$apollo.query({
        query: DEAL_USER_SEARCH_QUERY,
        variables: {
          email: searchVal,
        },
        fetchPolicy: "no-cache",
      });
      let data = results.data.dealUserSearch;
      this.linkUser.results = data.map((element) => {
        return {
          value: element.id,
          text: `${element.email} (${element.firstName} ${element.lastName})`,
        };
      });
      this.linkUser.loadingSearchResults = false;
    },
    onContactChangeSelect(contact) {
      this.linkUser.show = false;
    },
    async onContactChangeConfirm() {
      let data = {
        dealContactId: decodeId(this.deal.contact.id),
        userId: this.linkUser.select.value,
      };
      await this.$super(DealManagementMixin).updateDealContact(data);
      await this.$apollo.queries.deal.refetch();
    },
    onContactChangeCancel() {
      this.linkUser.show = true;
      this.linkUser.select = null;
    },
  },
  apollo: {
    deal: {
      query: DEAL_QUERY,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          id: this.dealId,
        };
      },
      skip() {
        return !this.dealId;
      },
    },
  },
};
</script>

<style scoped>
.v-data-table__wrapper {
  padding: 5px !important;
}
</style>
